/**
 * 非同期処理のアクション（asyncThunk）定義用ファイル。
 *
 *================================================
 * callThunkについて]
 *================================================
 *
 *   コンポーネントからdispatchするときは、必ずcallThunk経由でasyncThunkをコールしてください。
 *   callThunk内でcsrf tokenを発行済みかチェックして、発行済みでなければtoken発行用のBFFをコールしています。
 *
 *================================================
 * thunk内部からのdispatchとエラーハンドリングについて
 **================================================
 *
 * --------------------------------------
 *   基本的にthunk内部からのdispatchは行わない
 * --------------------------------------
 *   基本的にthunk内部からのdispatchは行わないでください。
 *   思わぬ副作用が発生したり、UTが複雑化するデメリットがあるからです。
 *
 * --------------------------------------
 * どうしてもasyncThunk内からdispatchしたい時
 * --------------------------------------
 *   どうしてもasyncThunk内からdispatchしたい時は、dispatchWrapperを経由してdispatchしてください。
 *   またasyncThunk内で発生したエラーは、rejectWithFormattedError経由で、formatErrorメソッドのフォーマットでrejectしてください。
 *   そうすることで、ネストされたasyncThunkで発生したエラーが、そのままcallThunkのrejectアクションに渡されるので、
 *   axiosErrorSliceは、callThunkをobserveするだけで済みます。
 *
 *   普通にdispatchすると、呼び元のasyncThunkにおいて、dispatch対象のasyncThunkで発生した例外をcatchできません。
 *   dispatchWrapperは、以下のエラーをGmoAskErrorクラスとしてthrowしてくれます。
 *     ① axiosがthrowしたエラー
 *     ② コール対象のasyncThunkから、rejectWithFormattedError経由で渡されたエラーオブジェクト。
 *     ③ コール対象のasyncThunk内で発生した例外。
 *
 *     ③について、
 *     基本的にasyncThunk内の処理は、全てtry ~ catchで囲み、catch内で明示的にrejectWithFormattedErrorにエラーを渡すのが望ましいが、
 *     明示的にrejectせずとも、catchされないエラーが存在すると、asyncThunkが自動的にrejectしてくれ、発生したエラーがrejectアクションに渡される。
 *     dispatchWrapperはそういったエラーも拾って、GmoAskErrorクラスとしてthrowしてくれます。
 *
 *   formatErrorメソッドのフォーマット以外の値をrejectで渡したい時は、
 *   dispatchWrapper・rejectWithFormattedErrorを使用せず、自前でエラーハンドリングを実装してください。
 */
import { createAsyncThunk } from '@reduxjs/toolkit';
import { createAgesGenderKeyObjs, culcTotal } from '@functional';
import GmoAskError from '~/errors/GmoAskError';
import { get, post, put, formatError } from '~/apiTs/utils';
import { ORIGINS } from '~/constants/api';
import { SURVEY_STATUSES, COUNTRY_CDS, DELIVERY_DATE, AGE_DIVIDE_TYPES } from '~/constants';

/**
 * asyncThunkからdispatchするときは、必ずこれでラップして呼ぶこと。
 * 普通にdispatchしたら例外が発生しても、rejectアクションのpayloadが普通のレスポンスとして返却されるため、例外をcatchできない。
 *
 * こちらを呼ぶと、dispatchしたactionがrejectされたら例外を投げてくれるので、
 * 呼び元でcatchして、必要に応じてrejectしたりすることが可能です。
 *
 * @param {*} dispatchAction thunkAPIでdispatchする処理を返却するコールバック関数。
 * @returns 第一引数であるdispatchActionのレスポンス。
 */
export const dispatchWrapper = async (dispatchAction) => {
  try {
    const res = await dispatchAction();
    // axiosのエラーの場合、payloadにAxios errorというnameを持ったエラークラスが存在している。
    // axios以外のエラーも、明示的にcatchしてrejectWithValueされた場合は、action.payloadにエラーが入っている。
    // （rejectWithValueした場合は、action.error.messageに"Rejected"という文字列が入っている）
    if (res?.payload?.name === 'Axios error' || res?.error?.message === 'Rejected') {
      throw new GmoAskError(
        res?.payload?.name,
        res?.payload?.message,
        res?.payload?.data,
        res?.payload?.status
      );
    }

    // エラーを明示的にrejectWithValueしなかった場合、res.errorは存在するが、payloadが存在しない。
    /* istanbul ignore next 今のところ明示的にrejectWithValueしていないthunkが存在しないため */
    if (res?.error && res.payload === undefined) {
      throw res.error;
    }

    return res;
  } catch (error) {
    // axios以外のエラーで、明示的にrejectValueされていない場合。
    // dispatchAction処理内のtry ~ catchしていない場所で、ReferenceErrorなどが発生した場合は、ここで拾う。
    console.error('something happened in dispatchWrapper.\nerror: ', error);
    throw new GmoAskError(error?.name, error?.message, error?.data, error?.status);
  }
};

/**
 * 基本的にasyncThunk内で例外を投げるときは、
 * このメソッドで、共通のフォーマットのエラーオブジェクトをpayloadとしてrejectしてください。
 * 普通にrejectWithValueすると、オブジェクトじゃないエラークラスなどがrejectアクションのpayloadにセットされ、
 * non-serializable valuesエラーが発生することがある。
 *
 * asyncThunk内のtry ~ catch内で発生した
 *  - 通常のエラー
 *  - axiosのエラー
 *  - dispatchWrapperがthrowしたエラー
 * を全て同じフォーマットのエラーオブジェクトに詰め替える。
 */
const rejectWithFormattedError = (thunkAPI, error) => {
  console.error('something happened in async thunk action.\nerror: ', error);
  // formatしてrejectする。
  return thunkAPI.rejectWithValue(formatError(error));
};

/**
 *  CSRF発行
 */
export const generateCsrfToken = createAsyncThunk(
  'appCommon/generateCsrfToken',
  async (_, thunkAPI) => {
    try {
      const response = await get({
        url: `${ORIGINS.GMOASK_BFF}/csrf-token`,
      });
      return response;
    } catch (error) {
      return rejectWithFormattedError(thunkAPI, error);
    }
  }
);

/**
 * thunkActionを呼ぶ前に必ずcsrfTokenが発行済みかを確認する。
 * 発行済みでなければ、発行後に対象のthunkActionをコールする。
 *
 * 使い方:
 *   引数に
 *     { action: 呼びたいthunkAction, payload: 呼びたいthunkActionの引数オブジェクト }
 *   を渡してください。
 *
 * 例:
 *   dispatch(
 *     callThunk({
 *       action: fetchSavedDeliverySettings,
 *       payload: { surveyId: 1 },
 *     })
 *   );
 */
export const callThunk = createAsyncThunk(
  'appCommon/callThunk',
  async ({ action, payload }, thunkAPI) => {
    try {
      const { isGeneratedCsrfToken } = thunkAPI.getState().appCommon;

      if (!isGeneratedCsrfToken) {
        await dispatchWrapper(() => thunkAPI.dispatch(generateCsrfToken()));
      }
      const res = await dispatchWrapper(() => thunkAPI.dispatch(action(payload)));
      return res.payload;
    } catch (error) {
      return rejectWithFormattedError(thunkAPI, error);
    }
  }
);

export const fetchSurveys = createAsyncThunk(
  'survey/fetchSurveys',
  async ({ surveyStatusIds }, thunkAPI) => {
    try {
      const response = await post({
        url: `${ORIGINS.GMOASK_BFF}/handover/gmoask`,
        params: {
          apiUri: '/surveys',
          parameter: JSON.stringify({
            surveyStatusIds,
          }),
          httpMethod: 'GET',
        },
      });
      return response;
    } catch (error) {
      return rejectWithFormattedError(thunkAPI, error);
    }
  }
);

export const fetchSurveysSelectByAdmin = createAsyncThunk(
  'surveys/fetchSurveysSelectByAdmin',
  async (params, thunkAPI) => {
    try {
      const response = await post({
        url: `${ORIGINS.GMOASK_BFF}/handover/gmoask`,
        params: {
          apiUri: '/surveys/select_by_admin',
          parameter:
            params === undefined
              ? null
              : JSON.stringify({
                  ...params,
                }),
          httpMethod: 'GET',
        },
      });
      return response;
    } catch (error) {
      return rejectWithFormattedError(thunkAPI, error);
    }
  }
);

const updateSurveyInner = async (update, thunkAPI) => {
  // stateから更新前のsurvey objectを取得
  const { survey } = thunkAPI.getState();
  // survey.idのkey名をsurveyIdに変更
  let parameter = { ...survey, surveyId: survey.id };
  delete parameter.id;
  // 更新前のobjectを更新用データで上書き（更新用データに存在するキーの箇所のみ）。
  parameter = JSON.stringify({
    ...parameter,
    ...update,
  });

  const { result: response } = await post({
    url: `${ORIGINS.GMOASK_BFF}/handover/gmoask`,
    params: {
      apiUri: '/surveys',
      parameter,
      httpMethod: 'PUT',
    },
  });
  return {
    survey: response,
  };
};

/**
 * 調査情報を更新するAPI。
 * BFFとAPIが1対1なのでhandover BFFによって
 * @see https://docs.google.com/spreadsheets/d/1Fk5TgH07m2MkK9SCsVg2t7rY5wkq_alC/edit#gid=1766032831
 */
export const updateSurvey = createAsyncThunk('survey/updateSurvey', async (update, thunkAPI) => {
  try {
    return await updateSurveyInner(update, thunkAPI);
  } catch (error) {
    return rejectWithFormattedError(thunkAPI, error);
  }
});

const fetchAreasInner = async () => {
  const { result: response } = await get({
    url: `${ORIGINS.GMOASK_BFF}/area`,
    params: {
      countryCd: COUNTRY_CDS.JAPAN,
      t: null,
    },
  });
  return response;
};

export const fetchAreas = createAsyncThunk('areas/fetchAreas', async (_, thunkAPI) => {
  try {
    return await fetchAreasInner();
  } catch (error) {
    return rejectWithFormattedError(thunkAPI, error);
  }
});

/**
 * 対象者検索ボタンが押された時、またはsurvey詳細（配信設定）画面を過去データから復帰するときに呼ばれる。
 * 普通の検索時と過去データからの復帰時でreducerの挙動が異なる箇所があるので、reducerにisReconstructingフラグを渡してあげる。
 */
export const fetchEstimatedDeliveryTargetCounts = createAsyncThunk(
  'quotas/fetchEstimatedDeliveryTargetCounts',
  // eslint-disable-next-line default-param-last
  async ({ isReconstructing = false } = {}, thunkAPI) => {
    try {
      const {
        current: { gender, age, areas, specialConditions },
      } = thunkAPI.getState().qualifications;

      const { result: response } = await post({
        url: `${ORIGINS.GMOASK_BFF}/estimated_delivery_target/counts`,
        params: {
          gender,
          ageFrom: age.from,
          ageTo: age.to,
          ageDivideType: age.divideType,
          areaCds:
            areas.checkedAreaCds.length === 0
              ? null
              : [areas.checkedAreaCds.map((areaCd) => [areaCd])],
          specialConditions:
            specialConditions.forApi.length === 0 ? null : specialConditions.forApi,
        },
      });

      // quotasを年齢・性別キーのオブジェクトに変換しておく
      const [ages, quotas] = createAgesGenderKeyObjs(response.quotas);
      return {
        ages,
        quotas,
        isReconstructing,
      };
    } catch (error) {
      return rejectWithFormattedError(thunkAPI, error);
    }
  }
);

export const createSurvey = createAsyncThunk(
  'survey/createSurvey',
  async ({ name, url, numberOfQuestions, type }, thunkAPI) => {
    try {
      const parameter = {
        name,
        url,
        numberOfQuestions: Number(numberOfQuestions),
        type,
      };

      const response = await post({
        url: `${ORIGINS.GMOASK_BFF}/handover/gmoask`,
        params: {
          apiUri: '/surveys',
          parameter: JSON.stringify(parameter),
          httpMethod: 'POST',
        },
      });
      return response;
    } catch (error) {
      return rejectWithFormattedError(thunkAPI, error);
    }
  }
);

/**
 * fetchSavedDeliverySettingsでAPIから取得した保存済み配信設定データを調整するクラス。
 * @param {*} deliverySettings 保存済み配信設定データ
 */
class SavedDeliverySettingAdjuster {
  constructor(deliverySettings) {
    this.deliverySettings = deliverySettings;
  }

  adjustQualifications() {
    return {
      ...this.deliverySettings.qualifications,
      specialConditions: JSON.parse(this.deliverySettings.qualifications.specialConditions),
      region: !this.deliverySettings.qualifications.region
        ? []
        : this.deliverySettings.qualifications.region.split(','),
    };
  }

  /**
   * 保存済み配信設定の回答希望数と設問数から見積もり金額を計算（API経由）
   *
   * @param {*} targetSamples deliverySettings.quotasから生成した年齢-性別をキーとした回答希望数のオブジェクト
   */
  async culcPriceFromSavedDeliverySetting(targetSamples) {
    const {
      survey: { numberOfQuestions },
    } = this.deliverySettings;

    const targetSamplesTotal = culcTotal({ items: targetSamples, isObject: true });
    const {
      result: { priceTotal },
    } = await post({
      url: `${ORIGINS.GMOASK_BFF}/handover/gmoask`,
      params: {
        apiUri: process.env.REACT_APP_CALCSURVEY_URI,
        parameter: JSON.stringify({
          targetSamples: Number(targetSamplesTotal),
          numberOfQuestions: Number(numberOfQuestions),
        }),
        httpMethod: 'GET',
      },
    });
    return Number(priceTotal);
  }
}

/**
 * surveyの詳細画面（配信設定画面）や申し込み画面のリロード時に最初に呼ばれるAction。
 * 調査情報や割付の設定条件、調査のステータスによっては回答状況などが、BFFによってまとめられて返ってくる。
 * @see https://docs.google.com/spreadsheets/d/1W-qpWgGRvRjWvW-uhjkNawi4PNKGOzl4/edit#gid=1766032831
 */
export const fetchSavedDeliverySettings = createAsyncThunk(
  'survey/fetchSavedDeliverySettings',
  async ({ surveyId }, thunkAPI) => {
    try {
      // fetchAreasをdispatchして地域を取得。
      const areas = await fetchAreasInner();

      const { result: deliverySettings } = await get({
        url: `${ORIGINS.GMOASK_BFF}/delivery_setting`,
        params: { surveyId },
      });

      // qualificationsをstore用に調整
      const Adjuster = new SavedDeliverySettingAdjuster(deliverySettings);
      deliverySettings.qualifications = Adjuster.adjustQualifications();

      // quotasが存在しない時は、支払い情報や回答数もnullになっている。
      // （qualificationsのスペパネ用データだけ常に値が入っているので、↑で調整している）
      // APIレスポンス以外の、本来このActionで生成されるデータは初期値にしてあげて早期リターン。
      if (deliverySettings.quotas.length === 0) {
        return {
          ...areas,
          ...deliverySettings,
          ages: [],
          targetSamples: {},
          targetSamplesTotal: 0,
          collectCounts: {},
        };
      }

      // 保存済みのquotasからは、回答希望数（targetSamples）と実回答数（collectCounts）が必要なので、
      // 年齢・性別をキーとした回答希望数オブジェクトを作成して破棄する。
      const [ages, targetSamples] = createAgesGenderKeyObjs(
        deliverySettings.quotas,
        'targetSamples'
      );

      const [_, collectCounts] = createAgesGenderKeyObjs(deliverySettings.quotas, 'collectCount');
      delete deliverySettings.quotas;

      // アンケートステータスが審査NGの場合、それとも
      // deliverySettings.surveyPaymentsがnullの時は見積もり金額計算してdeliverySettings.surveyPaymentsにセットしてあげる
      if (
        deliverySettings.surveyPayments === null ||
        deliverySettings.survey.surveyStatusId === SURVEY_STATUSES.REJECTED
      ) {
        deliverySettings.surveyPayments = {
          ...deliverySettings.surveyPayments,
          estimatePriceTotal: await Adjuster.culcPriceFromSavedDeliverySetting(targetSamples),
        };
      }

      return {
        ...areas,
        ...deliverySettings,
        ages,
        targetSamples,
        targetSamplesTotal: culcTotal({ items: targetSamples, isObject: true }),
        collectCounts,
      };
    } catch (error) {
      // 通常のaxiosコール、またはネストしたdispatch内で発生したエラーをpayloadとしてrejectする。
      return rejectWithFormattedError(thunkAPI, error);
    }
  }
);

export const passwordResetRequestUser = createAsyncThunk(
  'passwordReset/requestUser',
  async ({ email }, thunkAPI) => {
    try {
      const { result: response } = await post({
        url: `${ORIGINS.GMOASK_BFF}/users/reset_password`,
        params: {
          email,
        },
      });
      return response;
    } catch (error) {
      return rejectWithFormattedError(thunkAPI, error);
    }
  }
);

export const passwordResetRequestAdmin = createAsyncThunk(
  'passwordReset/requestAdmin',
  async ({ email }, thunkAPI) => {
    try {
      const { result: response } = await post({
        url: `${ORIGINS.GMOASK_BFF}/administrators/reset_password`,
        params: {
          email,
        },
      });
      return response;
    } catch (error) {
      return rejectWithFormattedError(thunkAPI, error);
    }
  }
);

export const updateUserPassword = createAsyncThunk(
  'passwordReset/updateUser',
  async ({ password, token }, thunkAPI) => {
    try {
      const response = await post({
        url: `${ORIGINS.GMOASK_BFF}/users/verify_reset_password`,
        params: {
          password,
          token,
        },
      });
      return response;
    } catch (error) {
      return rejectWithFormattedError(thunkAPI, error);
    }
  }
);

export const updateAdminPassword = createAsyncThunk(
  'passwordReset/updateAdmin',
  async ({ password, token }, thunkAPI) => {
    try {
      const response = await post({
        url: `${ORIGINS.GMOASK_BFF}/administrators/verify_reset_password`,
        params: {
          password,
          token,
        },
      });
      return response;
    } catch (error) {
      return rejectWithFormattedError(thunkAPI, error);
    }
  }
);

/**
 * 回答希望数内訳が変更 → 保存された時に呼ばれるAction。
 * 割付を保存すると同時に、見積もり金額計算APIで新しい見積もり金額を取得する。
 */
export const updatedTargetSamples = createAsyncThunk(
  'quotas/updatedTargetSamples',
  async ({ targetSamples, numberOfQuestions }, thunkAPI) => {
    try {
      const {
        survey,
        qualifications: { onSearched: qualifications },
        quotas: { quotas },
      } = thunkAPI.getState();

      // 割付保存
      const quotasParam = Object.keys(quotas).map((key) => ({
        gender: quotas[key].gender,
        ageFrom: quotas[key].ageFrom,
        ageTo: quotas[key].ageTo,
        targetSamples: targetSamples[key],
      }));

      await post({
        url: `${ORIGINS.GMOASK_BFF}/handover/gmoask`,
        params: {
          apiUri: '/surveys/quotas',
          parameter: JSON.stringify({
            surveyId: survey.id,
            qualifications: {
              gender: qualifications.gender,
              ageFrom: qualifications.age.from,
              ageTo: qualifications.age.to,
              shouldDivideAge: qualifications.age.divideType !== AGE_DIVIDE_TYPES.NONE,
              region: qualifications.areas.checkedAreaCds
                .slice() // store値を破壊しないようにsliceしてソートする
                .sort((a, b) => a - b)
                .join(','),
              specialConditions: JSON.stringify({
                forApi: qualifications.specialConditions.forApi,
              }),
            },
            quotas: quotasParam,
          }),
          httpMethod: 'POST',
        },
      });

      // 見積もり金額計算
      const targetSamplesTotal = culcTotal({ items: targetSamples, isObject: true });
      const { result: response } = await post({
        url: `${ORIGINS.GMOASK_BFF}/handover/gmoask`,
        params: {
          apiUri: process.env.REACT_APP_CALCSURVEY_URI,
          parameter: JSON.stringify({
            targetSamples: Number(targetSamplesTotal),
            numberOfQuestions: Number(numberOfQuestions),
          }),
          httpMethod: 'GET',
        },
      });
      return {
        targetSamples,
        targetSamplesTotal,
        estimatePriceTotal: Number(response.priceTotal),
      };
    } catch (error) {
      return rejectWithFormattedError(thunkAPI, error);
    }
  }
);

/**
 * 設問数が変更 → 保存された時に呼ばれるAction。
 * surveyの設問数を保存すると同時に、見積もり金額計算APIで新しい見積もり金額を取得する。
 */
export const updatedNumberOfQuestions = createAsyncThunk(
  'survey/updatedNumberOfQuestions',
  async ({ numberOfQuestions, targetSamplesTotal }, thunkAPI) => {
    try {
      const { survey } = await updateSurveyInner(
        {
          numberOfQuestions,
        },
        thunkAPI
      );

      // targetSamplesTotalが0の時はAPIでエラーになるので早期リターン
      if (Number(targetSamplesTotal) === 0) {
        return {
          survey,
          estimatePriceTotal: 0,
        };
      }

      const { result: response } = await post({
        url: `${ORIGINS.GMOASK_BFF}/handover/gmoask`,
        params: {
          apiUri: process.env.REACT_APP_CALCSURVEY_URI,
          parameter: JSON.stringify({
            targetSamples: Number(targetSamplesTotal),
            numberOfQuestions: Number(numberOfQuestions),
          }),
          httpMethod: 'GET',
        },
      });

      return {
        survey,
        estimatePriceTotal: Number(response.priceTotal),
      };
    } catch (error) {
      return rejectWithFormattedError(thunkAPI, error);
    }
  }
);

export const fetchDefaultDeliveryDate = createAsyncThunk(
  'survey/defaultDeliveryDate',
  async (_, thunkAPI) => {
    try {
      const { result: response } = await post({
        url: `${ORIGINS.GMOASK_BFF}/handover/gmoask`,
        params: {
          apiUri: '/available_delivery_start_date',
          parameter: JSON.stringify({
            businessDays: DELIVERY_DATE.DEFAULT_BUSINESS_DAYS,
          }),
          httpMethod: 'GET',
        },
      });

      return response;
    } catch (error) {
      return rejectWithFormattedError(thunkAPI, error);
    }
  }
);

/**
 * ユーザーログイン
 */
export const signinUser = createAsyncThunk(
  'user/signined',
  async ({ email, password }, thunkAPI) => {
    try {
      const response = await post({
        url: `${ORIGINS.GMOASK_BFF}/users/login`,
        params: {
          email,
          password,
        },
      });
      return response;
    } catch (error) {
      return rejectWithFormattedError(thunkAPI, error);
    }
  }
);

/*
 * ユーザーに紐づく請求書データを取得するAPIを呼ぶ。
 */
export const fetchInvoiceRecipient = createAsyncThunk(
  'invoiceRecipient/get',
  async ({ userId }, thunkAPI) => {
    try {
      const { result: response } = await post({
        url: `${ORIGINS.GMOASK_BFF}/handover/gmoask`,
        params: {
          apiUri: '/invoice_recipients',
          parameter: JSON.stringify({
            userId,
          }),
          httpMethod: 'GET',
        },
      });
      return response;
    } catch (error) {
      return rejectWithFormattedError(thunkAPI, error);
    }
  }
);

/**
 * 請求先を作成するAPI。
 * BFFとAPIが1対1なのでhandover BFFによって
 * @see https://docs.google.com/spreadsheets/d/1GIhqYOu7pY3VX-WKNrh53XsmZdaSla1s/edit#gid=1766032831
 */
export const createInvoiceRecipient = createAsyncThunk(
  'invoiceRecipient/create',
  async (payload, thunkAPI) => {
    try {
      // stateからinvoiceRecipient objectを取得
      const { invoiceRecipient } = thunkAPI.getState();

      const parameter = {
        ...invoiceRecipient,
        ...payload,
      };
      delete parameter.id;

      const { result: response } = await post({
        url: `${ORIGINS.GMOASK_BFF}/handover/gmoask`,
        params: {
          apiUri: '/invoice_recipients',
          parameter: JSON.stringify(parameter),
          httpMethod: 'POST',
        },
      });
      return response;
    } catch (error) {
      return rejectWithFormattedError(thunkAPI, error);
    }
  }
);

/**
 * 請求先を更新するAPI。
 * BFFとAPIが1対1なのでhandover BFFによって
 * @see https://docs.google.com/spreadsheets/d/1Mc8wFt3E6DeP4ZHw_ZO-ba9BkzkMTZsy/edit#gid=1766032831
 */
export const updateInvoiceRecipient = createAsyncThunk(
  'invoiceRecipient/update',
  async (update, thunkAPI) => {
    try {
      // stateから更新前のinvoiceRecipient objectを取得
      const { invoiceRecipient } = thunkAPI.getState();

      let parameter = {
        ...invoiceRecipient,
        invoiceRecipientId: invoiceRecipient.id,
      };
      delete parameter.id;

      // 更新前のobjectを更新用データで上書き（更新用データに存在するキーの箇所のみ）。
      parameter = {
        ...parameter,
        ...update,
      };

      const { result: response } = await post({
        url: `${ORIGINS.GMOASK_BFF}/handover/gmoask`,
        params: {
          apiUri: '/invoice_recipients',
          parameter: JSON.stringify(parameter),
          httpMethod: 'PUT',
        },
      });
      return response;
    } catch (error) {
      return rejectWithFormattedError(thunkAPI, error);
    }
  }
);

/**
 * 支払い情報登録API。
 * BFFとAPIが1対1なのでhandover BFFによって
 * @see https://docs.google.com/spreadsheets/d/1G6uhPGu-bKJYCuSRjoW9qZdlnLsygKQB/edit#gid=1766032831
 */
export const createSurveyPayments = createAsyncThunk(
  'apply/createSurveyPayments',
  async (parameter, thunkAPI) => {
    try {
      const response = await post({
        url: `${ORIGINS.GMOASK_BFF}/handover/gmoask`,
        params: {
          apiUri: '/surveys/estimate_payments',
          parameter: JSON.stringify(parameter),
          httpMethod: 'POST',
        },
      });
      return response;
    } catch (error) {
      return rejectWithFormattedError(thunkAPI, error);
    }
  }
);

/**
 * 支払い情報更新API。
 * BFFとAPIが1対1なのでhandover BFFによって
 * @see https://docs.google.com/spreadsheets/d/1M7qabAjOQa5fEelwwffvaun81cbz09Xj/edit#gid=1766032831
 */
export const updateSurveyPayments = createAsyncThunk(
  'apply/updateSurveyPayments',
  async (parameter, thunkAPI) => {
    try {
      const response = await post({
        url: `${ORIGINS.GMOASK_BFF}/handover/gmoask`,
        params: {
          apiUri: '/surveys/estimate_payments',
          parameter: JSON.stringify(parameter),
          httpMethod: 'PUT',
        },
      });
      return response;
    } catch (error) {
      return rejectWithFormattedError(thunkAPI, error);
    }
  }
);

/**
 * 申し込みAPI。
 * BFFとAPIが1対1なのでhandover BFFによって
 * @see https://docs.google.com/spreadsheets/d/1G7JGA1_UQFcNjiLAFpJjN6eRNE9SP4Xc/edit#gid=1055732628
 */
export const applySurvey = createAsyncThunk('apply/applySurvey', async (parameter, thunkAPI) => {
  try {
    const response = await post({
      url: `${ORIGINS.GMOASK_BFF}/handover/gmoask`,
      params: {
        apiUri: '/surveys/apply',
        parameter: JSON.stringify(parameter),
        httpMethod: 'PUT',
      },
    });
    return response;
  } catch (error) {
    return rejectWithFormattedError(thunkAPI, error);
  }
});

/**
 * クレカ仮決済完了API。
 * @see https://docs.google.com/spreadsheets/d/1Oqk-CNQCi8kmYFIjjqfcxb9IC5bjgDD_/edit#gid=1766032831
 */
export const sendEpsilonOrderNumber = createAsyncThunk(
  'epsilonResult/card',
  async ({ orderNumber }, thunkAPI) => {
    try {
      const response = await post({
        url: `${ORIGINS.GMOASK_BFF}/survey_payments/card/complete`,
        params: {
          orderNumber,
        },
      });
      return response;
    } catch (error) {
      return rejectWithFormattedError(thunkAPI, error);
    }
  }
);

// 郵便番号から住所を取得する 決済方法選択画面
export const getAddressesByZipCodeInvoice = createAsyncThunk(
  'invoiceRecipient/getAddressesByZipCode',
  async (zipCode, thunkAPI) => {
    try {
      const { result } = await get({
        url: `${ORIGINS.GMOASK_BFF}/addresses`,
        params: {
          zipCode,
        },
      });
      return {
        result,
        zipCode,
      };
    } catch (error) {
      return rejectWithFormattedError(thunkAPI, error);
    }
  }
);

// 郵便番号から住所を取得する ユーザー新登録画面
export const getAddressesByZipCodeSignup = createAsyncThunk(
  'user/getAddressesByZipCode',
  async (zipCode, thunkAPI) => {
    try {
      const { result: response } = await get({
        url: `${ORIGINS.GMOASK_BFF}/addresses`,
        params: {
          zipCode,
        },
      });
      return response;
    } catch (error) {
      return rejectWithFormattedError(thunkAPI, error);
    }
  }
);

/*
 * ログアウト
 */
export const logoutAdmin = createAsyncThunk('admin/loggedOut', async (_, thunkAPI) => {
  try {
    await post({
      url: `${ORIGINS.GMOASK_BFF}/administrators/logout`,
    });
    return true;
  } catch (error) {
    return rejectWithFormattedError(thunkAPI, error);
  }
});

/*
 * ログアウト
 */
export const logoutUser = createAsyncThunk('user/loggedOut', async (_, thunkAPI) => {
  try {
    await post({
      url: `${ORIGINS.GMOASK_BFF}/users/logout`,
    });
    return true;
  } catch (error) {
    return rejectWithFormattedError(thunkAPI, error);
  }
});

/**
 * クレカ仮決済開始API。
 * @see https://docs.google.com/spreadsheets/d/1MvK4ymZDlfCzt-u2HdIMu86xmPYu18Kg/edit?rtpof=true#gid=1055732628
 */
export const cardPayment = createAsyncThunk('apply/cardPayment', async (surveyId, thunkAPI) => {
  try {
    const { result: response } = await post({
      url: `${ORIGINS.GMOASK_BFF}/survey_payments/card`,
      params: {
        surveyId,
      },
    });
    return response;
  } catch (error) {
    return rejectWithFormattedError(thunkAPI, error);
  }
});

/**
 * 調査ステータス変更。
 * 承認API、審査NG APIのどちらかを実行する。
 * BFFとAPIが1対1なのでhandover BFFを使う。
 */
export const changeSurveyStatus = createAsyncThunk(
  'survey/changeSurveyStatus',
  async ({ surveyId, surveyStatusId, checkedRejectCategories, rejectReason }, thunkAPI) => {
    try {
      let resStatusId;
      if (surveyStatusId === SURVEY_STATUSES.APPROVED) {
        // 承認APIを実行
        const { result } = await post({
          url: `${ORIGINS.GMOASK_BFF}/handover/gmoask`,
          params: {
            apiUri: '/surveys/approve',
            parameter: JSON.stringify({
              surveyId,
            }),
            httpMethod: 'PUT',
          },
        });
        resStatusId = result.surveyStatusId;
      }

      if (surveyStatusId === SURVEY_STATUSES.REJECTED) {
        const rejectCategories = [];
        Object.keys(checkedRejectCategories).forEach((id) => {
          if (checkedRejectCategories[id]) {
            rejectCategories.push(id);
          }
        });
        // 却下APIを実行
        const { result } = await put({
          url: `${ORIGINS.GMOASK_BFF}/surveys/reject`,
          params: {
            surveyId,
            rejectCategories,
            rejectReason,
          },
        });
        resStatusId = result.surveyStatusId;
      }

      // 新しいステータスを返却。
      return {
        surveyStatusId: resStatusId,
      };
    } catch (error) {
      return rejectWithFormattedError(thunkAPI, error);
    }
  }
);

/*
 * 認証済みのユーザー情報を取得する
 */
export const getAuthenticatedUser = createAsyncThunk(
  'user/getAuthenticatedUser',
  async (_, thunkAPI) => {
    try {
      const result = await get({
        url: `${ORIGINS.GMOASK_BFF}/users/authenticated_user`,
      });
      return result;
    } catch (error) {
      return rejectWithFormattedError(thunkAPI, error);
    }
  }
);

/*
 * Get all administrator and role data of each administrator
 */
export const fetchAdminAndRoles = createAsyncThunk(
  'administrators/fetchAdminAndRoles',
  async (_, thunkAPI) => {
    try {
      const response = await post({
        url: `${ORIGINS.GMOASK_BFF}/handover/gmoask`,
        params: {
          apiUri: '/administrators',
          httpMethod: 'GET',
        },
      });
      return response;
    } catch (error) {
      return rejectWithFormattedError(thunkAPI, error);
    }
  }
);

/*
 * Get all roles
 */
export const fetchRoles = createAsyncThunk('admin/fetchRoles', async (_, thunkAPI) => {
  try {
    const response = await post({
      url: `${ORIGINS.GMOASK_BFF}/handover/gmoask`,
      params: {
        apiUri: '/roles',
        httpMethod: 'GET',
      },
    });
    return response;
  } catch (error) {
    return rejectWithFormattedError(thunkAPI, error);
  }
});

/*
 * ユーザの反社チェック更新
 */
export const updateAntiSocialCheck = createAsyncThunk(
  'users/updateAntiSocialCheck',
  async (params, thunkAPI) => {
    try {
      const result = await post({
        url: `${ORIGINS.GMOASK_BFF}/users/anti_social_check`,
        params,
      });
      return result;
    } catch (error) {
      return rejectWithFormattedError(thunkAPI, error);
    }
  }
);

/*
 * ユーザの反社チェック承認更新
 */
export const updateAntiSocialCheckApproval = createAsyncThunk(
  'users/updateAntiSocialCheckApproval',
  async (params, thunkAPI) => {
    try {
      const result = await post({
        url: `${ORIGINS.GMOASK_BFF}/users/anti_social_check_approval`,
        params,
      });
      return result;
    } catch (error) {
      return rejectWithFormattedError(thunkAPI, error);
    }
  }
);

/*
 * 与信チェック更新
 */
export const updateCreditCheck = createAsyncThunk(
  'users/updateCreditCheck',
  async (params, thunkAPI) => {
    try {
      const result = await post({
        url: `${ORIGINS.GMOASK_BFF}/users/credit_check`,
        params,
      });
      return result;
    } catch (error) {
      return rejectWithFormattedError(thunkAPI, error);
    }
  }
);

/*
 * 自主退会
 */
export const withdrawUser = createAsyncThunk('users/withdrawUser', async (params, thunkAPI) => {
  try {
    const response = await post({
      url: `${ORIGINS.GMOASK_BFF}/users/withdraw`,
      params,
    });
    return response;
  } catch (error) {
    return rejectWithFormattedError(thunkAPI, error);
  }
});

/*
 * 請求書支払いできるかどうかを取得する。
 */
export const fetchAllowedPaymentType = createAsyncThunk(
  'user/fetchAllowedPaymentType',
  async (_, thunkAPI) => {
    try {
      const response = await post({
        url: `${ORIGINS.GMOASK_BFF}/handover/gmoask`,
        params: {
          apiUri: '/users/allowed_payment_type',
          parameter: null,
          httpMethod: 'GET',
        },
      });
      return response;
    } catch (error) {
      return rejectWithFormattedError(thunkAPI, error);
    }
  }
);

/**
 * ユーザーの画面表示・非表示情報を取得する。
 */
export const fetchUserScreenVisibilitySettings = createAsyncThunk(
  'user/fetchUserScreenVisibilitySettings',
  async (_, thunkAPI) => {
    try {
      const response = await post({
        url: `${ORIGINS.GMOASK_BFF}/handover/gmoask`,
        params: {
          apiUri: '/users/screen_visibility_settings',
          parameter: null,
          httpMethod: 'GET',
        },
      });
      return response;
    } catch (error) {
      return rejectWithFormattedError(thunkAPI, error);
    }
  }
);

/**
 * ユーザーの画面表示・非表示を設定する
 */
export const createUserScreenVisibilitySettings = createAsyncThunk(
  'user/createUserScreenVisibilitySettings',
  async (parameter, thunkAPI) => {
    try {
      const response = await post({
        url: `${ORIGINS.GMOASK_BFF}/handover/gmoask`,
        params: {
          apiUri: '/users/screen_visibility_settings',
          parameter: JSON.stringify(parameter),
          httpMethod: 'POST',
        },
      });
      return response;
    } catch (error) {
      return rejectWithFormattedError(thunkAPI, error);
    }
  }
);

export const updateAdministratorRoles = createAsyncThunk(
  'admin/administrator_roles',
  async (parameter, thunkAPI) => {
    try {
      const response = await post({
        url: `${ORIGINS.GMOASK_BFF}/handover/gmoask`,
        params: {
          apiUri: '/administrator_roles',
          parameter: JSON.stringify(parameter),
          httpMethod: 'POST',
        },
      });
      return response;
    } catch (error) {
      return rejectWithFormattedError(thunkAPI, error);
    }
  }
);
